import React, { useEffect } from "react";
import BackHeader from "@components/header/BackHeader";
import styled from "@emotion/styled";
import InputWithMessage from "@components/input/InputWithMessage";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { changePasswordSchema } from "@libs/zod/zodVaildation";
import BasicButton from "@components/button/BasicButton";
import { useMutation } from "@tanstack/react-query";
import { useApiRequest } from "@utils/ApiRequest";
import { useNavigate } from "react-router-dom";

const Form = styled.form({
  display: "flex",
  flexDirection: "column",
  height: "calc(100vh - 5.6rem)",
  padding: "1.2rem 2rem 3.6rem",
  justifyContent: "space-between",
});

const FormWrapper = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "4.8rem",
});

const Title = styled.h3(({ theme }) => ({
  fontSize: "2rem",
  fontWeight: theme.fontWeight.bold,
  color: theme.color.black,
}));

const InputWrapper = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "1.8rem",
});

const ChangePassword = () => {
  const method = useForm({
    resolver: zodResolver(changePasswordSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      current_password: "",
      new_password: "",
      confirm_password: "",
    },
  });

  const { patchChangePassword } = useApiRequest();
  const navigate = useNavigate();

  const { mutate, isSuccess } = useMutation({
    mutationFn: (data: { current_password: string; new_password: string }) =>
      patchChangePassword({
        old_password: data.current_password,
        new_password: data.new_password,
      }),

    onSuccess: () => {
      navigate("/my-page");
    },

    onError: () => {
      setError("current_password", {
        message: "",
      });
    },
  });

  const {
    handleSubmit,
    setError,
    trigger,
    watch,
    formState: { errors, dirtyFields },
  } = method;

  // new_password가 변경될 때마다 confirm_password validation 실행
  const newPassword = watch("new_password");
  useEffect(() => {
    if (watch("confirm_password")) {
      trigger("confirm_password");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newPassword]);

  const onSubmit = (data: any) => {
    mutate({
      current_password: data.current_password,
      new_password: data.new_password,
    });
  };

  return (
    <FormProvider {...method}>
      <BackHeader title="계정관리" />

      <Form onSubmit={handleSubmit(onSubmit)}>
        <FormWrapper>
          <Title>{"비밀번호를 변경해주세요."}</Title>

          <InputWrapper>
            <InputWithMessage
              name="current_password"
              type="password"
              message="기존 비밀번호 확인"
              success={isSuccess} // 비밀번호 변경 성공 시, 기존비밀번호 확인 가능
            />

            <InputWithMessage
              name="new_password"
              type="password"
              message="영문 + 숫자 포함 8-20자 이내"
              success={dirtyFields.new_password && !errors.new_password}
            />

            <InputWithMessage
              name="confirm_password"
              type="password"
              message="비밀번호 바꾸기 재확인"
              success={dirtyFields.confirm_password && !errors.confirm_password}
            />
          </InputWrapper>
        </FormWrapper>
        <BasicButton type="submit" title="변경하기" />
      </Form>
    </FormProvider>
  );
};

export default ChangePassword;
